<template>
    <div class="grid">
        <div class="col-12">
            <div class="card text-center" v-if="permissionDenied">
                <h4>A kért oldal megtekintéséhez nincs jogosultsága!</h4>
            </div>
            <div class="card" v-if="!permissionDenied">
                <Toast/>

                <DataTable ref="dt" :value="invitations" dataKey="id"
                           :paginator="true" :rows="25" :filters="filters" :loading="loading"
                           paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                           :rowsPerPageOptions="[10,25,50,100]"
                           currentPageReportTemplate="" responsiveLayout="scroll" filterDisplay="row"
                           v-model:filters="filters"
                           :globalFilterFields="['invitingUserName', 'invitingUserEmail', 'invitedEmail', 'status', 'acceptedUserName']">
                    <template #header>
                        <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
                            <h5 class="m-0">Szülői meghívók <br> <strong><small>Összesen: {{ total }}</small></strong></h5>
                            <span class="block mt-2 md:mt-0 p-input-icon-left">
                                <i class="pi pi-search"/>
                                <InputText v-model="filters['global'].value" placeholder="Keresés..."/>
                            </span>
                        </div>
                    </template>

                    <Column field="id" header="#" :sortable="true" headerStyle="width:3%; min-width:4rem;">
                        <template #body="slotProps">
                            {{ slotProps.data.id }}
                        </template>
                    </Column>

                    <Column field="invitingUserName" header="Meghívó" :sortable="true" headerStyle="min-width:10rem;"
                            :showFilterMenu="false">
                        <template #body="slotProps">
                            {{ slotProps.data.invitingUserName }}
                            <br><small class="text-500">{{ slotProps.data.invitingUserEmail }}</small>
                        </template>
                        <template #filter="{filterModel,filterCallback}">
                            <InputText type="text" v-model="filterModel.value" @input="filterCallback()"
                                       class="p-column-filter" placeholder="Keresés..."/>
                        </template>
                    </Column>

                    <Column field="invitedEmail" header="Meghívott e-mail" :sortable="true" headerStyle="min-width:10rem;"
                            :showFilterMenu="false">
                        <template #body="slotProps">
                            {{ slotProps.data.invitedEmail }}
                            <template v-if="slotProps.data.acceptedUserName">
                                <br><small class="text-500">Elfogadta: {{ slotProps.data.acceptedUserName }}</small>
                            </template>
                        </template>
                        <template #filter="{filterModel,filterCallback}">
                            <InputText type="text" v-model="filterModel.value" @input="filterCallback()"
                                       class="p-column-filter" placeholder="Keresés..."/>
                        </template>
                    </Column>

                    <Column field="status" header="Státusz" :sortable="true" headerStyle="min-width:8rem;"
                            :showFilterMenu="false">
                        <template #body="slotProps">
                            <Tag :value="statusLabel(slotProps.data)" :severity="statusSeverity(slotProps.data)"/>
                        </template>
                        <template #filter="{filterModel,filterCallback}">
                            <Dropdown v-model="filterModel.value" :options="statusOptions"
                                      optionLabel="label" optionValue="value"
                                      @change="filterCallback()" class="p-column-filter"
                                      placeholder="Összes" :showClear="true"/>
                        </template>
                    </Column>

                    <Column field="childrenNames" header="Gyermekek" headerStyle="min-width:10rem;">
                        <template #body="slotProps">
                            <span v-for="(child, index) in slotProps.data.children" :key="child.id">
                                {{ child.name }}<span v-if="index < slotProps.data.children.length - 1">, </span>
                            </span>
                        </template>
                    </Column>

                    <Column field="expiresAt" header="Lejárat" :sortable="true" headerStyle="min-width:8rem;">
                        <template #body="slotProps">
                            {{ slotProps.data.expiresAt }}
                        </template>
                    </Column>

                    <Column field="createdAt" header="Létrehozva" :sortable="true" headerStyle="min-width:8rem;">
                        <template #body="slotProps">
                            {{ slotProps.data.createdAt }}
                        </template>
                    </Column>

                    <Column headerStyle="min-width:14rem;">
                        <template #body="slotProps">
                            <Button v-if="slotProps.data.status === 'pending' && !slotProps.data.isExpired"
                                    icon="pi pi-check" class="p-button-rounded p-button-success mr-2"
                                    v-tooltip.top="'Elfogadás'" @click="confirmAccept(slotProps.data)"/>
                            <Button v-if="slotProps.data.status === 'pending' && !slotProps.data.isExpired"
                                    icon="pi pi-times" class="p-button-rounded p-button-warning mr-2"
                                    v-tooltip.top="'Elutasítás'" @click="confirmDecline(slotProps.data)"/>
                            <Button icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2"
                                    v-tooltip.top="'Törlés'" @click="confirmDelete(slotProps.data)"/>
                        </template>
                    </Column>

                    <template #empty>
                        Nincs egyetlen meghívó sem.
                    </template>
                </DataTable>

                <!-- Accept dialog -->
                <Dialog v-model:visible="acceptDialog" :style="{width: '450px'}" header="Megerősítés" :modal="true">
                    <div class="flex align-items-center justify-content-center">
                        <i class="pi pi-question-circle mr-3" style="font-size: 2rem; color: var(--green-500)" />
                        <span>Biztosan elfogadja a meghívót?<br>
                            <small>Meghívó: <b>{{ selectedInvitation.invitingUserName }}</b></small><br>
                            <small>Meghívott: <b>{{ selectedInvitation.invitedEmail }}</b></small>
                        </span>
                    </div>
                    <template #footer>
                        <Button label="Nem" icon="pi pi-times" class="p-button-text" @click="acceptDialog = false"/>
                        <Button label="Igen" icon="pi pi-check" class="p-button-success" @click="acceptInvitation"
                                :loading="loading"/>
                    </template>
                </Dialog>

                <!-- Decline dialog -->
                <Dialog v-model:visible="declineDialog" :style="{width: '450px'}" header="Megerősítés" :modal="true">
                    <div class="flex align-items-center justify-content-center">
                        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--orange-500)" />
                        <span>Biztosan elutasítja a meghívót?<br>
                            <small>Meghívó: <b>{{ selectedInvitation.invitingUserName }}</b></small><br>
                            <small>Meghívott: <b>{{ selectedInvitation.invitedEmail }}</b></small>
                        </span>
                    </div>
                    <template #footer>
                        <Button label="Nem" icon="pi pi-times" class="p-button-text" @click="declineDialog = false"/>
                        <Button label="Igen" icon="pi pi-check" class="p-button-warning" @click="declineInvitation"
                                :loading="loading"/>
                    </template>
                </Dialog>

                <!-- Delete dialog -->
                <Dialog v-model:visible="deleteDialog" :style="{width: '450px'}" header="Megerősítés" :modal="true">
                    <div class="flex align-items-center justify-content-center">
                        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
                        <span>Biztosan törölni szeretné a meghívót?<br>
                            <small>Meghívó: <b>{{ selectedInvitation.invitingUserName }}</b></small><br>
                            <small>Meghívott: <b>{{ selectedInvitation.invitedEmail }}</b></small>
                        </span>
                    </div>
                    <template #footer>
                        <Button label="Nem" icon="pi pi-times" class="p-button-text" @click="deleteDialog = false"/>
                        <Button label="Igen" icon="pi pi-check" class="p-button-danger" @click="deleteInvitation"
                                :loading="loading"/>
                    </template>
                </Dialog>
            </div>
        </div>
    </div>
</template>

<script>
import {FilterMatchMode} from 'primevue/api';
import InvitationService from "@/service/InvitationService";

export default {
    data() {
        return {
            total: 0,
            invitations: null,
            selectedInvitation: {},
            acceptDialog: false,
            declineDialog: false,
            deleteDialog: false,
            filters: {},
            loading: true,
            permissionDenied: true,
            statusOptions: [
                { label: 'Függőben', value: 'pending' },
                { label: 'Elfogadva', value: 'accepted' },
                { label: 'Elutasítva', value: 'declined' },
                { label: 'Lejárt', value: 'expired' },
            ],
        }
    },
    invitationService: null,
    created() {
        this.invitationService = new InvitationService();
        this.initFilters();
    },
    mounted() {
        let user = JSON.parse(localStorage.getItem('userData'));

        if (user.role === 1) {
            this.permissionDenied = true;
        } else {
            this.permissionDenied = false;
            this.getAll();
        }
    },
    methods: {
        getAll() {
            this.loading = true;
            this.invitationService.getAll().then(response => {
                this.total = response.data.total;
                this.invitations = response.data.data;
                this.loading = false;
            });
        },
        statusLabel(data) {
            if (data.status === 'pending' && data.isExpired) return 'Lejárt';
            const map = {
                'pending': 'Függőben',
                'accepted': 'Elfogadva',
                'declined': 'Elutasítva',
                'expired': 'Lejárt',
            };
            return map[data.status] || data.status;
        },
        statusSeverity(data) {
            if (data.status === 'pending' && data.isExpired) return 'warning';
            const map = {
                'pending': 'info',
                'accepted': 'success',
                'declined': 'danger',
                'expired': 'warning',
            };
            return map[data.status] || null;
        },
        confirmAccept(invitation) {
            this.selectedInvitation = { ...invitation };
            this.acceptDialog = true;
        },
        confirmDecline(invitation) {
            this.selectedInvitation = { ...invitation };
            this.declineDialog = true;
        },
        confirmDelete(invitation) {
            this.selectedInvitation = { ...invitation };
            this.deleteDialog = true;
        },
        acceptInvitation() {
            this.loading = true;
            this.invitationService.accept(this.selectedInvitation.id).then(() => {
                this.acceptDialog = false;
                this.selectedInvitation = {};
                this.getAll();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Sikeres művelet!',
                    detail: 'A meghívás elfogadása sikeres volt.',
                    life: 5000
                });
            }).catch(e => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Sikertelen művelet!',
                    detail: e.response?.data?.error || 'Hiba történt.',
                    life: 6000,
                });
                this.loading = false;
            });
        },
        declineInvitation() {
            this.loading = true;
            this.invitationService.decline(this.selectedInvitation.id).then(() => {
                this.declineDialog = false;
                this.selectedInvitation = {};
                this.getAll();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Sikeres művelet!',
                    detail: 'A meghívás elutasítása sikeres volt.',
                    life: 5000
                });
            }).catch(e => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Sikertelen művelet!',
                    detail: e.response?.data?.error || 'Hiba történt.',
                    life: 6000,
                });
                this.loading = false;
            });
        },
        deleteInvitation() {
            this.loading = true;
            this.invitationService.delete(this.selectedInvitation.id).then(() => {
                this.deleteDialog = false;
                this.selectedInvitation = {};
                this.getAll();
                this.$toast.add({
                    severity: 'success',
                    summary: 'Sikeres művelet!',
                    detail: 'A meghívás törlése sikeres volt.',
                    life: 5000
                });
            }).catch(e => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Sikertelen művelet!',
                    detail: e.response?.data?.error || 'Hiba történt.',
                    life: 6000,
                });
                this.loading = false;
            });
        },
        initFilters() {
            this.filters = {
                'global': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'invitingUserName': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'invitedEmail': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'status': {
                    value: null,
                    matchMode: FilterMatchMode.EQUALS
                },
            }
        }
    }
}
</script>

<style scoped lang="scss">
@import '../../assets/demo/badges.scss';
</style>

